import { useMemo, useState, type ReactElement } from 'react';
import type { MessageTemplate, Uuid } from '@zenda/api-contract';
import { Icon } from './Icon';
import { Link } from 'react-router';
import { useTip } from './Tip';
import { useT } from '../lib/i18n';
import { useClientTemplates, useSendPatientTemplate, useSendTemplate } from '../lib/queries';
import '../styles/template-avulso.css';

/** As `{{1}}`, `{{2}}`… que o template pede, em ordem e sem repetir. */
function variaveisDe(corpo: string): readonly number[] {
  const achadas = new Set<number>();
  for (const m of corpo.matchAll(/\{\{(\d+)\}\}/g)) achadas.add(Number(m[1]));
  return [...achadas].sort((a, b) => a - b);
}

/**
 * Reabrir a conversa passadas as 24 h.
 *
 * ── Por que isto precisa existir na tela da conversa ──
 * Fechada a janela, a Cloud API só aceita template. A tela dizia isso e não
 * oferecia o caminho: sobrava a nota interna, que a paciente nunca vê. Quem
 * precisa falar com ela é a atendente, e o lugar onde ela está é aqui.
 *
 * ── Só os aprovados ──
 * Template `PENDING` ou `REJECTED` volta erro da Meta no envio. Mostrar os três
 * estados e deixar o erro acontecer seria transferir para a atendente uma
 * conferência que a lista já sabe fazer.
 */
export function ReabrirComTemplate({
  clientId,
  conversationId,
  contactId = null,
  onEnviado,
}: {
  readonly clientId: Uuid;
  /** `null` com `contactId`: o primeiro envio para um paciente sem conversa. */
  readonly conversationId: Uuid | null;
  readonly contactId?: Uuid | null;
  readonly onEnviado: (conversationId: Uuid) => void;
}): ReactElement {
  const tip = useTip();
  const r = useT().reabrir;
  const lista = useClientTemplates(clientId);
  const naConversa = useSendTemplate(conversationId);
  const aoPaciente = useSendPatientTemplate(clientId, contactId);
  const enviar = conversationId === null ? aoPaciente : naConversa;
  const [escolhido, setEscolhido] = useState<MessageTemplate | null>(null);
  const [valores, setValores] = useState<readonly string[]>([]);

  // Sem corpo nao ha o que enviar nem o que pre-visualizar: a Meta devolve
  // `body: null` para template que so tem cabecalho ou botoes.
  const aprovados = useMemo(
    () => (lista.data?.templates ?? []).filter((t) => t.status === 'APPROVED' && t.body !== null),
    [lista.data],
  );

  const variaveis = escolhido === null ? [] : variaveisDe(escolhido.body ?? '');
  const faltaPreencher = variaveis.some((_, i) => (valores[i] ?? '').trim() === '');

  /** O texto como a paciente vai receber. */
  const previa =
    escolhido === null
      ? ''
      : (escolhido.body ?? '').replace(/\{\{(\d+)\}\}/g, (inteiro, n: string) => {
          const v = (valores[Number(n) - 1] ?? '').trim();
          return v === '' ? inteiro : v;
        });

  function escolher(t: MessageTemplate): void {
    setEscolhido(t);
    setValores(variaveisDe(t.body ?? '').map(() => ''));
  }

  function enviarAgora(t: MessageTemplate): void {
    const payload = {
      templateName: t.name,
      language: t.language,
      bodyParams: valores.map((v) => v.trim()),
    };
    const pronto = (id: Uuid): void => {
      setEscolhido(null);
      setValores([]);
      onEnviado(id);
    };
    if (conversationId === null) {
      aoPaciente.mutate(payload, { onSuccess: (r) => pronto(r.conversationId) });
    } else {
      naConversa.mutate(payload, { onSuccess: () => pronto(conversationId) });
    }
  }

  if (lista.isPending) return <p className="rct__vazio">{r.carregando}</p>;

  if (aprovados.length === 0) {
    return (
      <div className="rct__vazio">
        <p>{conversationId === null ? r.semAprovadoPrimeira : r.semAprovadoJanela}</p>
        <Link to={`/clientes/${clientId}?aba=templates`} className="btn btn--ghost btn--sm">
          {r.criarTemplate}
        </Link>
      </div>
    );
  }

  return (
    <div className="rct">
      <select
        className="input input--sm"
        value={escolhido?.id ?? ''}
        onChange={(e) => {
          const t = aprovados.find((x) => x.id === e.target.value);
          if (t !== undefined) escolher(t);
        }}
      >
        <option value="">{r.escolha}</option>
        {aprovados.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} · {t.language}
          </option>
        ))}
      </select>

      {escolhido === null ? null : (
        <>
          {variaveis.length === 0 ? null : (
            <div className="rct__vars">
              {variaveis.map((n, i) => (
                <label key={n} className="rct__var">
                  <span className="num">{`{{${n}}}`}</span>
                  <input
                    className="input input--xs"
                    value={valores[i] ?? ''}
                    maxLength={200}
                    onChange={(e) =>
                      setValores((v) => v.map((x, j) => (j === i ? e.target.value : x)))
                    }
                  />
                </label>
              ))}
            </div>
          )}

          {/* A prévia é o que o paciente lê. Mandar template é irreversível e
              cobrado — ver antes de enviar é o mínimo. */}
          <p className="rct__previa">{previa}</p>

          {enviar.isError ? (
            <p className="rct__erro">
              <Icon name="alert" size={13} />
              {enviar.error.message}
            </p>
          ) : null}

          <button
            type="button"
            className="btn btn--primary btn--sm btn--block"
            disabled={faltaPreencher || enviar.isPending}
            onClick={() => enviarAgora(escolhido)}
            {...tip(conversationId === null ? r.dicaComeca : r.dicaReabre)}
          >
            {enviar.isPending ? r.enviando : r.enviar}
          </button>
        </>
      )}
    </div>
  );
}
